import React, {
  PropsWithChildren,
  MouseEvent,
  useMemo,
  useCallback,
} from 'react';

import { useIntl } from 'react-intl';
import clsx from 'clsx';

import { Enum } from '@utils/enum';
import { messageToString } from '@utils/message';

import { Message, Link, Loader, Icon, IconType, LinkProps } from '@atoms';
import { Tooltip, TooltipProps } from '@molecules/tooltip';

import classes from './button.module.scss';

const buttonTypes = Enum('primary', 'filled', 'outline', 'white', 'link');
const buttonSizes = Enum('normal', 'small', 'tiny');

export type ButtonType = keyof typeof buttonTypes;
export type ButtonSize = keyof typeof buttonSizes;
type HTMLType = 'button' | 'reset' | 'submit';

export type ButtonProps = {
  className?: string;
  message?: Message;
  icon?: IconType;
  type?: ButtonType;
  size?: ButtonSize;
  link?: Omit<LinkProps, 'className'>;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  htmlType?: HTMLType;
  tooltip?: TooltipProps;
};

const formTypes: HTMLType[] = ['reset', 'submit'];

type Props = PropsWithChildren<ButtonProps>;

export const Button = ({
  children,
  message,
  icon,
  type = 'primary',
  size = 'normal',
  link,
  disabled = false,
  loading = false,
  onClick,
  htmlType,
  tooltip,
  className,
}: Props) => {
  const intl = useIntl();
  /*
   *CONTENT
   */
  const content = useMemo(() => {
    let content = null;

    if (children) {
      content = children;
    } else if (message) {
      content = messageToString(message, intl);
    } else if (icon) {
      content = <Icon icon={icon} />;
    }

    content = <span className={classes.content}>{content}</span>;

    if (loading) {
      content = (
        <>
          <Loader className={classes.loader} button />
          {content}
        </>
      );
    }

    return content;
  }, [children, message, icon, loading, intl]);

  /*
   *STYLE
   */
  const wrapperClass = useMemo(
    () =>
      clsx(
        classes.button,
        classes[type],
        classes[`size--${size}` as keyof typeof classes],
        {
          [classes.disabled]: disabled,
          [classes.loading]: loading,
          [classes.withIcon]: Boolean(icon),
        },
        'gtm-button',
        className,
      ),
    [className, type, size, disabled, loading, icon],
  );

  /*
   *HANDLE CLICK
   */
  const _onClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (disabled) {
        event.preventDefault();
        return;
      }
      if (link) return;

      if (!htmlType || !formTypes.includes(htmlType)) {
        event.preventDefault();
      }

      if (onClick) {
        onClick();
      }
    },
    [disabled, link, htmlType, htmlType, onClick],
  );

  return useMemo(() => {
    let component = null;

    if (!link) {
      component = (
        <button className={wrapperClass} onClick={_onClick} type={htmlType}>
          {content}
        </button>
      );
    } else {
      component = (
        <Link className={wrapperClass} onClick={onClick} {...link}>
          {content}
        </Link>
      );
    }

    if (tooltip) {
      component = <Tooltip {...tooltip}>{component}</Tooltip>;
    }

    return component;
  }, [wrapperClass, htmlType, _onClick, content, link, tooltip]);
};
